// Admin/manager — choose which sections go into the 7am morning task email.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sunrise } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MorningEmailConfigCard = ({ domainId = null }) => {
  const [cfg, setCfg] = useState(null);
  const [optedIn, setOptedIn] = useState(0);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [stats, setStats] = useState(null);
  const [newDate, setNewDate] = useState('');

  useEffect(() => {
    axios.get(`${API}/admin/morning-email-stats${qs}`)
      .then((r) => setStats(r.data)).catch(() => setStats(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainId]);

  const loadPreview = async (kind = 'agent') => {
    setPreviewing(true);
    try {
      const url = kind === 'digest'
        ? `${API}/admin/supervisor-digest-preview${qs}`
        : `${API}/admin/morning-email-preview${qs}`;
      const r = await axios.get(url);
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    }
    setPreviewing(false);
  };

  const qs = domainId ? `?domain_id=${domainId}` : '';

  useEffect(() => {
    axios.get(`${API}/admin/morning-email-config${qs}`)
      .then((r) => { setCfg(r.data.config); setOptedIn(r.data.opted_in_users || 0); })
      .catch(() => setCfg(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainId]);

  const save = async (next) => {
    setCfg(next);
    try {
      await axios.patch(`${API}/admin/morning-email-config${qs}`, next);
      toast.success('Morning email settings updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const toggle = (key) => save({ ...cfg, [key]: !cfg[key] });

  const addHoliday = () => {
    if (!newDate) return;
    setNewDate('');
    if ((cfg.holidays || []).includes(newDate)) return;
    save({ ...cfg, holidays: [...(cfg.holidays || []), newDate].sort() });
  };

  const removeHoliday = (d) => save({ ...cfg, holidays: (cfg.holidays || []).filter((x) => x !== d) });

  const loadPack = async (pack) => {
    try {
      const r = await axios.post(`${API}/admin/morning-email-config/load-pack${qs}`, { pack });
      setCfg(r.data.config);
      toast.success(`${pack === 'national' ? 'AU national' : `${pack} + national`} holidays added — auto-tops-up each December`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load holiday pack');
    }
  };

  if (cfg === null) return null;

  const Row = ({ k, label, desc }) => (
    <label className="flex items-start gap-2.5 cursor-pointer py-1.5">
      <input type="checkbox" checked={!!cfg[k]} onChange={() => toggle(k)}
        className="mt-0.5 accent-teal-500" data-testid={`morning-email-${k}-checkbox`} />
      <span>
        <span className="block text-sm font-semibold">{label}</span>
        <span className="block text-xs opacity-60">{desc}</span>
      </span>
    </label>
  );

  return (
    <div className="rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/[0.03] text-slate-900 dark:text-white p-4 mt-6" data-testid="morning-email-config-card">
      <h3 className="font-bold text-sm flex items-center gap-2 mb-1">
        <Sunrise className="w-4 h-4 text-amber-500" /> Morning task email — content
      </h3>
      <p className="text-xs opacity-60 mb-2">
        Sent 7:00am AEST to users who opt in via the toggle in their sidebar
        ({optedIn} user{optedIn === 1 ? '' : 's'} opted in). Includes a one-click
        "Open my portal" login link (valid 24h).
      </p>
      <Row k="reassigned" label="Callbacks reassigned to them" desc="Deals a manager has handed to the user for follow-up" />
      <Row k="revivable" label="Revivable deals to call today" desc="Their ranked follow-ups due today (same list as the dashboard widget)" />
      <div className="border-t border-slate-100 dark:border-white/5 mt-2 pt-2" data-testid="quiet-hours-section">
        <p className="text-[10px] uppercase tracking-wide opacity-50 mb-0.5">Quiet hours</p>
        <Row k="skip_weekends" label="Skip weekends" desc="No morning emails on Saturday or Sunday" />
        <Row k="skip_holidays" label="Skip public holidays" desc="No morning emails on the dates listed below" />
        {cfg.skip_holidays && (
          <div className="ml-6 mt-1" data-testid="holiday-editor">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)}
                className="text-xs border border-slate-200 dark:border-white/10 rounded-md px-2 py-1 bg-transparent"
                data-testid="holiday-date-input" />
              <button onClick={addHoliday} disabled={!newDate}
                className="text-xs font-bold px-3 py-1 rounded-full bg-teal-600 text-white hover:bg-teal-500 disabled:opacity-40"
                data-testid="holiday-add-btn">Add date</button>
              {[['national', 'AU national'], ['QLD', 'QLD'], ['NSW', 'NSW'], ['VIC', 'VIC']].map(([pack, label]) => (
                <button key={pack} onClick={() => loadPack(pack)}
                  className={`text-xs font-semibold px-3 py-1 rounded-full border ${cfg.holiday_pack === pack
                    ? 'border-teal-400 bg-teal-50 text-teal-800 dark:bg-teal-500/10 dark:text-teal-300'
                    : 'border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-white/5'}`}
                  title={pack === 'national' ? 'National public holidays' : `National + ${pack} public holidays`}
                  data-testid={`holiday-pack-${pack.toLowerCase()}-btn`}>
                  + {label}
                </button>
              ))}
            </div>
            <p className="text-[10px] opacity-50 mb-2">
              Packs auto-top-up next year's dates every December{cfg.holiday_pack ? ` (current pack: ${cfg.holiday_pack})` : ''}.
              One-off days announced yearly (Ekka, AFL Grand Final Friday) need adding manually.
            </p>
            <div className="flex flex-wrap gap-1.5" data-testid="holiday-chip-list">
              {(cfg.holidays || []).map((d) => (
                <span key={d} className="inline-flex items-center gap-1 text-[11px] font-semibold bg-slate-100 dark:bg-white/10 rounded-full px-2 py-0.5" data-testid={`holiday-chip-${d}`}>
                  {d}
                  <button onClick={() => removeHoliday(d)} className="opacity-50 hover:opacity-100 font-bold" data-testid={`holiday-remove-${d}`}>×</button>
                </span>
              ))}
              {!(cfg.holidays || []).length && (
                <span className="text-[11px] opacity-50 italic">No holiday dates yet — add them above.</span>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="mt-2 flex items-center gap-2 flex-wrap">
        <button onClick={() => loadPreview('agent')} disabled={previewing}
          className="text-xs font-bold px-3.5 py-1.5 rounded-full bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-50 dark:bg-white/10 dark:hover:bg-white/20"
          data-testid="morning-email-preview-btn">
          {previewing ? 'Building preview…' : "Preview tomorrow's email"}
        </button>
        <button onClick={() => loadPreview('digest')} disabled={previewing}
          className="text-xs font-bold px-3.5 py-1.5 rounded-full border border-slate-300 dark:border-white/15 hover:bg-slate-50 dark:hover:bg-white/5 disabled:opacity-50"
          data-testid="supervisor-digest-preview-btn">
          Preview supervisor digest
        </button>
      </div>
      {preview && (
        <div className="mt-3 border border-slate-200 dark:border-white/10 rounded-lg overflow-hidden" data-testid="morning-email-preview">
          <div className="flex items-center justify-between px-3 py-2 bg-slate-50 dark:bg-white/5 text-xs">
            <span className="font-semibold truncate">
              {preview.subject || 'No preview available'}
              {preview.sample_user ? ` · sample: ${preview.sample_user}` : ''}
            </span>
            <button onClick={() => setPreview(null)} className="opacity-50 hover:opacity-100 font-bold ml-2" data-testid="morning-email-preview-close">✕</button>
          </div>
          {preview.note && <p className="px-3 py-1.5 text-[11px] text-amber-600 bg-amber-50 dark:bg-amber-500/10">{preview.note}</p>}
          {preview.html && (
            <div className="bg-white p-3 max-h-96 overflow-y-auto"
              dangerouslySetInnerHTML={{ __html: preview.html }} />
          )}
        </div>
      )}
      {stats && stats.sent > 0 && (
        <div className="mt-4 border-t border-slate-200 dark:border-white/10 pt-3" data-testid="morning-email-stats">
          <p className="text-xs font-bold mb-2">Engagement — last {stats.days} days</p>
          {stats.nudge_count > 0 && (
            <div className="rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 px-3 py-2 mb-2 text-xs text-amber-700 dark:text-amber-300" data-testid="morning-email-nudge-banner">
              <strong>{stats.nudge_count} user{stats.nudge_count === 1 ? " hasn't" : "s haven't"} opened a morning email all week</strong> — worth a word in person.
            </div>
          )}
          <div className="grid grid-cols-3 gap-2 mb-2">
            {[['Sent', stats.sent, ''], ['Opened', stats.opened, `${stats.open_rate}%`], ['Clicked through', stats.clicked, `${stats.click_rate}%`]].map(([l, v, pct]) => (
              <div key={l} className="rounded-lg bg-slate-50 dark:bg-white/5 px-3 py-2">
                <div className="text-lg font-bold tabular-nums">{v}{pct && <span className="text-xs font-normal opacity-50 ml-1">({pct})</span>}</div>
                <div className="text-[10px] uppercase tracking-wide opacity-50">{l}</div>
              </div>
            ))}
          </div>
          {(stats.trend || []).filter((w) => w.sent > 0).length > 1 && (
            <div className="mb-3" data-testid="morning-email-trend">
              <p className="text-[10px] uppercase tracking-wide opacity-50 mb-1">Week by week</p>
              {stats.trend.filter((w) => w.sent > 0).map((w) => (
                <div key={w.week} className="flex items-center gap-2 text-[11px] py-0.5" data-testid={`trend-week-${w.week}`}>
                  <span className="w-14 opacity-60 whitespace-nowrap">{w.start}</span>
                  <span className="w-12 tabular-nums opacity-60">{w.sent} sent</span>
                  <div className="flex-1 h-2.5 bg-slate-100 dark:bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full bg-teal-500/40 rounded-full" style={{ width: `${w.open_rate}%` }}>
                      <div className="h-full bg-teal-500 rounded-full" style={{ width: w.open_rate ? `${(w.click_rate / w.open_rate) * 100}%` : 0 }} />
                    </div>
                  </div>
                  <span className="w-24 tabular-nums text-right"><span className="text-teal-600 font-bold">{w.open_rate}%</span> <span className="opacity-50">open</span> · <span className="font-bold">{w.click_rate}%</span> <span className="opacity-50">click</span></span>
                </div>
              ))}
            </div>
          )}
          {(stats.users || []).length > 0 && (
            <table className="w-full text-xs">
              <thead><tr className="text-left opacity-50 uppercase text-[10px]">
                <th className="py-1">User</th><th className="py-1 text-center">Sent</th>
                <th className="py-1 text-center">Opened</th><th className="py-1 text-center">Clicked</th>
              </tr></thead>
              <tbody>
                {stats.users.map((u, i) => (
                  <tr key={i} className="border-t border-slate-100 dark:border-white/5" data-testid={`morning-email-stats-user-${i}`}>
                    <td className="py-1.5">
                      {u.name}
                      {u.needs_nudge && (
                        <span className="ml-1.5 inline-block text-[9px] font-bold uppercase tracking-wide text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-500/20 rounded-full px-1.5 py-0.5" data-testid={`nudge-badge-${i}`}>
                          No opens · 7d
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 text-center tabular-nums">{u.sent}</td>
                    <td className={`py-1.5 text-center tabular-nums ${u.opened ? 'text-teal-600 font-bold' : 'opacity-40'}`}>{u.opened}</td>
                    <td className={`py-1.5 text-center tabular-nums ${u.clicked ? 'text-teal-600 font-bold' : 'opacity-40'}`}>{u.clicked}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="text-[10px] opacity-50 mt-1.5">Opens use a tracking pixel (undercounts when images are blocked); clicks count "Open my portal" logins — the truest engagement signal.</p>
        </div>
      )}
      {stats && stats.sent === 0 && (
        <p className="mt-3 text-[11px] opacity-50 italic" data-testid="morning-email-stats-empty">No morning emails sent in the last {stats.days} days yet — engagement stats will appear here.</p>
      )}
    </div>
  );
};
